import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useUpdateTemplateMutation, useUpdateTaskMutation } from "../store/api/templates";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import { RepeatType, TaskTemplate, Task } from "../types";
import * as yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, Edit2, Save, X } from "lucide-react";

interface EditTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: TaskTemplate;
}

export default function EditTemplateModal({
  open,
  onOpenChange,
  template,
}: EditTemplateModalProps) {
  const { t } = useLanguage();
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const [updateTemplate, { isLoading: isUpdatingTemplate }] = useUpdateTemplateMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [tasks, setTasks] = useState<Task[]>(template.tasks);
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  // Track which existing tasks have been edited
  const [editedTaskIds, setEditedTaskIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [taskErrors, setTaskErrors] = useState<{ [key: string]: string }>({});
  const [taskFormErrors, setTaskFormErrors] = useState<{ [key: string]: string }>({});

  const isLoading = isUpdatingTemplate || isUpdatingTask;

  // Yup validation schema
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('settings.templateNameRequiredError'))
      .max(50, t('settings.templateNameMaxLength')),
    description: yup
      .string()
      .max(150, t('settings.templateDescMaxLength')),
  });

  // Task validation schema
  const taskValidationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('settings.taskNameRequiredError'))
      .max(50, t('settings.taskNameMaxLength')),
    description: yup
      .string()
      .max(150, t('settings.taskDescMaxLength')),
  });

  const validateForm = async (): Promise<boolean> => {
    try {
      await validationSchema.validate({ name, description }, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const validateAll = async (): Promise<boolean> => {
    const fieldValid = await validateForm();
    const formValid = tasks.length > 0;

    return fieldValid && formValid;
  };

  const validateTaskForm = async (): Promise<boolean> => {
    try {
      await taskValidationSchema.validate(editingTask, { abortEarly: false });
      setTaskFormErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setTaskFormErrors(newErrors);
      }
      return false;
    }
  };


  const [editingTask, setEditingTask] = useState<Partial<Omit<Task, "id" | "workspaceId" | "createdAt" | "updatedAt">>>(
    {
      name: "",
      description: "",
      recurrence: { type: "none" },
    }
  );

  // Load template data when modal opens or template changes
  useEffect(() => {
    if (open) {
      setName(template.name);
      setDescription(template.description || "");
      setTasks(template.tasks);
      setEditedTaskIds(new Set());
      setErrors({});
      setTaskErrors({});
      setTaskFormErrors({});
      // Reset editing state
      setEditingTaskIndex(null);
      setEditingTask({
        name: "",
        description: "",
        recurrence: { type: "none" },
      });
    }
  }, [template, open, t]);

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  const handleAddTask = () => {
    if (!validateTaskForm()) {
      return;
    }

    const newTask: Task = {
      id: `task-new-${Date.now()}`,
      workspaceId: currentWorkspace?.id || '',
      name: editingTask.name?.trim() || '',
      description: editingTask.description || "",
      recurrence: editingTask.recurrence || { type: "daily" },
      daysUntilDue: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setTasks([...tasks, newTask]);
    
    // Reset form
    setEditingTask({
      name: "",
      description: "",
      recurrence: { type: "daily" },
    });
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleEditTask = (index: number) => {
    const task = tasks[index];
    setEditingTaskIndex(index);
    setEditingTask({
      name: task.name,
      description: task.description,
      recurrence: task.recurrence,
    });
  };

  const handleUpdateTask = () => {
    if (editingTaskIndex === null) return;

    if (!validateTaskForm()) {
      return;
    }

    const existingTask = tasks[editingTaskIndex];
    const updatedTask: Task = {
      ...existingTask,
      name: editingTask.name?.trim() || '',
      description: editingTask.description || "",
      recurrence: editingTask.recurrence || { type: "daily" },
      updatedAt: new Date().toISOString(),
    };

    const updatedTasks = [...tasks];
    updatedTasks[editingTaskIndex] = updatedTask;
    setTasks(updatedTasks);

    // Track edited existing tasks (not new tasks with temp IDs)
    if (!existingTask.id.startsWith('task-new-')) {
      setEditedTaskIds(prev => new Set(prev).add(existingTask.id));
    }

    // 編集モードをリセット
    setEditingTaskIndex(null);
    setEditingTask({
      name: "",
      description: "",
      recurrence: { type: "daily" },
    });
  };

  const handleCancelEdit = () => {
    setEditingTaskIndex(null);
    setTaskErrors({});
    setTaskFormErrors({});
    setEditingTask({
      name: "",
      description: "",
      recurrence: { type: "none" },
    });
  };

  const handleSave = async () => {
    if (!currentWorkspace) {
      toast.error(t('workspace.noWorkspaceSelected'));
      return;
    }

    // Validate all (fields + business rules)
    const isValid = await validateAll();
    if (!isValid) {
      return;
    }

    try {
      // First, update any edited existing tasks
      const editedTasks = tasks.filter(task => 
        !task.id.startsWith('task-new-') && editedTaskIds.has(task.id)
      );
      
      for (const task of editedTasks) {
        await updateTask({
          workspaceId: currentWorkspace.id,
          taskId: task.id,
          data: {
            name: task.name,
            description: task.description || '',
            recurrence: task.recurrence,
            daysUntilDue: task.daysUntilDue || 0,
          },
        }).unwrap();
      }

      // Separate existing tasks (with real IDs) from new tasks (with temp IDs)
      const existingTaskIds = tasks
        .filter(task => !task.id.startsWith('task-new-'))
        .map(task => task.id);
      
      const newTasks = tasks
        .filter(task => task.id.startsWith('task-new-'))
        .map(task => ({
          name: task.name,
          description: task.description || '',
          recurrence: task.recurrence,
          daysUntilDue: task.daysUntilDue || 0,
        })) as any[];

      // Then update the template
      await updateTemplate({
        workspaceId: currentWorkspace.id,
        templateId: template.id,
        data: {
          name: name.trim(),
          description: description.trim(),
          task_ids: existingTaskIds,
          tasks: newTasks,
        },
      }).unwrap();

      toast.success(t('settings.templateUpdated'));
      
      // Reset editing state after successful save
      setEditingTaskIndex(null);
      setTaskErrors({});
      setTaskFormErrors({});
      setEditingTask({
        name: "",
        description: "",
        recurrence: { type: "none" },
      });

      onOpenChange(false);
    } catch (error) {
      handleApiError(error, t('settings.templateUpdateFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('settings.editTaskTemplate')}</DialogTitle>
          <DialogDescription>{t('settings.editTemplateDesc')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* テンプレート情報 */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2">{t('settings.templateNameRequired')}</Label>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // Clear error when user types
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                }}
                placeholder={t('settings.templateNamePlaceholder')}
                required={true}
                maxLength={50}
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
              
            </div>

            <div>
              <Label className="mb-2">{t('common.description')}</Label>
              <Textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  // Clear error when user types
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                placeholder={t('settings.templateDescPlaceholder')}
                maxLength={150}
                className={errors.description ? "border-red-500" : ""}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>
          </div>

          {/* Task List */}
          <div className="space-y-3">
            <Label className="mb-2">{t('settings.registeredTasks')} ({tasks.length}{t('settings.items')})</Label>
            {tasks.length > 0 && (
              <div className="space-y-2">
                {tasks.map((task, index) => (
                  <div
                    key={task.id || index}
                    className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-gray-900">{task.name}</div>
                      {task.description && (
                        <div className="text-xs text-gray-600 mt-0.5">
                          {task.description}
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                        <span>
                          {task.recurrence.type === "none" && t('tasks.none')}
                          {task.recurrence.type === "daily" && t('tasks.daily')}
                          {task.recurrence.type === "weekly" && t('tasks.weekly')}
                          {task.recurrence.type === "monthly" && t('tasks.monthly')}
                          {task.recurrence.type === "quarterly" && t('tasks.quarterly')}
                          {task.recurrence.type === "semi-annually" && t('tasks.semiAnnually')}
                          {task.recurrence.type === "yearly" && t('tasks.yearly')}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditTask(index)}
                    >
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveTask(index)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Task Edit Form */}
          {editingTaskIndex !== null && (
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="mb-2">{t('settings.editTaskLabel')}</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('settings.cancelEdit')}
                </Button>
              </div>
              
              <div>
                <Label className="mb-2">{t('settings.taskNameRequired')}</Label>
                <Input
                  value={editingTask.name || ""}
                  onChange={(e) => {
                    setEditingTask({ ...editingTask, name: e.target.value });
                    // Clear error when user types
                    if (taskFormErrors.name) {
                      setTaskFormErrors({ ...taskFormErrors, name: '' });
                    }
                  }}
                  placeholder={t('settings.taskNamePlaceholder')}
                  maxLength={50}
                  className={taskFormErrors.name ? "border-red-500" : ""}
                />
                {taskFormErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{taskFormErrors.name}</p>
                )}
              </div>

              <div>
                <Label className="mb-2">{t('tasks.taskSummary')}</Label>
                <Textarea
                  value={editingTask.description || ""}
                  onChange={(e) => {
                    setEditingTask({ ...editingTask, description: e.target.value });
                    // Clear error when user types
                    if (taskFormErrors.description) {
                      setTaskFormErrors({ ...taskFormErrors, description: '' });
                    }
                  }}
                  placeholder={t('settings.taskDescPlaceholder')}
                  rows={3}
                  maxLength={150}
                  className={taskFormErrors.description ? "border-red-500" : ""}
                />
                {taskFormErrors.description && (
                  <p className="mt-1 text-sm text-red-500">{taskFormErrors.description}</p>
                )}
              </div>

              <div>
                <Label className="mb-2">{t('settings.recurrenceSetting')}</Label>
                <Select
                  value={editingTask.recurrence?.type}
                  onValueChange={(value: "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semi-annually" | "yearly") =>
                    setEditingTask({
                      ...editingTask,
                      recurrence: { type: value },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('tasks.none')}</SelectItem>
                    <SelectItem value="daily">{t('tasks.daily')}</SelectItem>
                    <SelectItem value="weekly">{t('tasks.weekly')}</SelectItem>
                    <SelectItem value="monthly">{t('tasks.monthly')}</SelectItem>
                    <SelectItem value="quarterly">{t('tasks.quarterly')}</SelectItem>
                    <SelectItem value="semi-annually">{t('tasks.semiAnnually')}</SelectItem>
                    <SelectItem value="yearly">{t('tasks.yearly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleUpdateTask} className="flex-1" type="button">
                  <Save className="w-4 h-4 mr-2" />
                  {t('settings.updateTask')}
                </Button>
                <Button onClick={handleCancelEdit} variant="outline" className="flex-1" type="button">
                  <X className="w-4 h-4 mr-2" />
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                // Reset editing state when canceling
                setEditingTaskIndex(null);
                setTaskErrors({});
                setTaskFormErrors({});
                setEditingTask({
                  name: "",
                  description: "",
                  recurrence: { type: "none" },
                });
                onOpenChange(false);
              }} 
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
